import { Patient } from '@medplum/fhirtypes';
import { useEffect, useRef, useState } from 'react';
import { TimelinePaymentEvent, TimelinePaymentOrder, TimelineRefundEvent } from '@/components/Timeline/types';
import { showsBillingCards } from '@/services/Timeline/billingFilter';
import { useBenefitEvents, UseBenefitEventsResult } from './useBenefitEvents';
import { RefundRequestsState } from './useRefundRequests';

/** Stable empty lists, so dropping the cards does not regroup the timeline every render. */
const NO_PAYMENT_CARDS: (TimelinePaymentOrder | TimelinePaymentEvent)[] = [];
const NO_REFUND_CARDS: TimelineRefundEvent[] = [];

interface UseBillingTimelineCardsInput {
  patient: Patient | undefined;
  activeFilter: string;
  /** The one billing permission (BAR-2646): the chip, these cards and the panel's billing group. */
  canViewPayments: boolean;
  treatmentPaymentsEnabled: boolean;
  benefitEventsEnabled: boolean;
  /** From the page's single `usePaymentOrder`, which the header and the panel also read. */
  paymentOrderCards: (TimelinePaymentOrder | TimelinePaymentEvent)[];
  paymentOrderLoading: boolean;
  /**
   * From the page's single `useRefundRequests`, which the + Add menu and the refund composer also
   * read under every filter (BAR-3069). Absent for a role without payments access, which reads none.
   */
  refundRequests?: Pick<RefundRequestsState, 'cards' | 'loading'>;
}

export interface BillingTimelineCards {
  paymentCards: (TimelinePaymentOrder | TimelinePaymentEvent)[];
  refundCards: TimelineRefundEvent[];
  benefitEvents: UseBenefitEventsResult;
  /** A billing read is still in flight, so an empty Billing filter is not yet "no billing". */
  loading: boolean;
}

/**
 * The timeline's billing cards: payment order and events, refund requests (BAR-3052) and
 * employer benefit events (BAR-2593). They show under All and Billing only, and never for a
 * role without `canViewPayments` (BAR-2646).
 *
 * Benefit events are not asked for at all outside that. The payment order and the refund requests
 * are read by the page regardless, because the header, the Intake and billing panel and the + Add
 * menu need them under every filter, so only their cards are dropped here.
 */
export function useBillingTimelineCards({
  patient,
  activeFilter,
  canViewPayments,
  treatmentPaymentsEnabled,
  benefitEventsEnabled,
  paymentOrderCards,
  paymentOrderLoading,
  refundRequests,
}: UseBillingTimelineCardsInput): BillingTimelineCards {
  const shown = showsBillingCards(activeFilter, canViewPayments);

  // `usePaymentOrder` raises `loading` on every refresh, including the ones a payment
  // action awaits. Only its first read for this patient counts here, or each refresh
  // would swap the Billing filter for a skeleton and unmount the card mid-action.
  const patientId = patient?.id;
  const orderReadStartedFor = useRef<string | undefined>(undefined);
  const [orderSettledFor, setOrderSettledFor] = useState<string>();
  useEffect(() => {
    if (!patientId) return;
    if (paymentOrderLoading) {
      orderReadStartedFor.current = patientId;
    } else if (orderReadStartedFor.current === patientId) {
      setOrderSettledFor(patientId);
    }
  }, [patientId, paymentOrderLoading]);
  const orderFirstReadPending =
    canViewPayments && treatmentPaymentsEnabled && !!patientId && orderSettledFor !== patientId;

  const refunds = shown && treatmentPaymentsEnabled ? refundRequests : undefined;
  // Flag-gated so nothing queries Claim/ClaimResponse before the policies that grant it are deployed.
  const benefitEvents = useBenefitEvents(shown && benefitEventsEnabled ? patient : undefined);
  return {
    paymentCards: shown ? paymentOrderCards : NO_PAYMENT_CARDS,
    refundCards: refunds?.cards ?? NO_REFUND_CARDS,
    benefitEvents,
    loading: shown && (orderFirstReadPending || !!refunds?.loading || benefitEvents.loading),
  };
}
